<template>
    <v-card>
        <v-card-title>{{ `${$props.subscriptionSchedule.subscription_id ? 'Edit' : 'Add'} Subscription Schedule` }} </v-card-title>

        <v-divider />

        <v-card-text class="mt-4">

            <v-form ref="subscriptionScheduleForm" class="fill-height" :lazy-validation="true" v-model="subscriptionScheduleFormValid">

                <v-row>
                    <v-col cols="12">
                        <v-combobox multiple chips autocomplete="off" color="#c42111" v-model="$props.subscriptionSchedule.recipients" :items="users" label="Recipients" hide-selected item-value="user_id" item-text="search" :rules="[$store.state.validationFuncs.requiredList($props.subscriptionSchedule.recipients, 'At least one recipient')]">
                            <template v-slot:selection="data">
                                <template v-if="data.item.type == 'user'">
                                    <v-chip v-bind="data.attrs" :input-value="data.selected" close @click="data.select" @click:close="removeRecipient(data.item)" :color="data.item.user_page_email ? validUserRecipientColor : noEmailColor">
                                        <v-avatar left>
                                            <v-img :src="data.item.userImage" v-if="data.item.userImage" />
                                            <v-icon v-else>mdi-account-circle</v-icon>
                                        </v-avatar>
                                        {{ data.item.user_fullname }}
                                    </v-chip>
                                </template>
                                <template v-else>
                                    <v-chip v-bind="data.attrs" :input-value="data.selected" close @click="data.select" @click:close="removeRecipient(data.item)" :color="(typeof $store.state.validationFuncs.email(data.item) === 'string') ? noEmailColor : validUserRecipientColor">
                                        {{ data.item }}
                                    </v-chip>
                                </template>
                            </template>

                            <template v-slot:item="data">
                                <v-list-item-avatar>
                                    <v-img :src="data.item.userImage" v-if="data.item.userImage" />
                                    <v-icon x-large v-else>mdi-account-circle</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>{{ data.item.user_fullname }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.item.user_username }} <span :style="{color: noEmailColor}" v-if="!data.item.user_page_email">(no email)</span></v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-combobox>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="6">
                        <v-select label="Recurs" v-model="$props.subscriptionSchedule.recur_type" color="#c42111" :items="recurOptions" />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-menu v-model="editScheduleTime" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="$props.subscriptionSchedule.recur_time" color="#c42111" label="At This Time" prepend-icon="access_time" readonly v-on="on" :rules="[$store.state.validationFuncs.required($props.subscriptionSchedule.recur_time, 'Time is required')]"></v-text-field>
                            </template>
                            <v-time-picker v-model="$props.subscriptionSchedule.recur_time" color="#c42111" :hourOnly="false" :format="'24hr'" use-seconds scrollable />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="2" class="text-left mt-6">
                        <span>Repeat every: </span>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-select class="text-left" v-model="$props.subscriptionSchedule.repeat_frequency" color="#c42111" :items="recurFrequencyOptions" />
                    </v-col>
                    <v-col cols="12" sm="2" class="text-left mt-6">
                        {{recurTypeText}}
                    </v-col>
                    <v-col cols="12" sm="6" v-if="$props.subscriptionSchedule.recur_type == 'weekly' || $props.subscriptionSchedule.recur_type == 'monthly'">
                        <v-select label="Repeat on" v-model="repeatOnWeekdays" color="#c42111" :items="weekDayOptions" multiple v-if="$props.subscriptionSchedule.recur_type == 'weekly'" :rules="[validation.weekdayRequired($props.subscriptionSchedule.recur_type, repeatOnWeekdays)]" />
                        <v-select label="Repeat on" v-model="repeatOnMonthDay" color="#c42111" :items="monthDayOptions" v-if="$props.subscriptionSchedule.recur_type == 'monthly'" :rules="[$store.state.validationFuncs.required(repeatOnMonthDay, 'Day of month')]" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="5">
                        <v-menu v-model="editStartDate" :close-on-content-click="true" transition="scale-transition" offset-y min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="$props.subscriptionSchedule.start_date" color="#c42111" label="Starts On" prepend-icon="event" readonly v-on="on" :rules="[$store.state.validationFuncs.required($props.subscriptionSchedule.start_date, 'Start Date')]" />
                            </template>
                            <v-date-picker v-model="$props.subscriptionSchedule.start_date" color="#c42111" scrollable />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-switch color="#c42111" :label="neverEnds ? 'Never Ends' : 'Until'" v-model="neverEnds" />
                    </v-col>
                    <v-col cols="12" sm="5">
                        <v-menu v-model="editEndDate" :close-on-content-click="true" transition="scale-transition" offset-y min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="$props.subscriptionSchedule.end_date" color="#c42111" label="Ends On" prepend-icon="event" readonly v-on="on" :disabled="neverEnds" :rules="[validation.dateOrder($props.subscriptionSchedule.start_date, $props.subscriptionSchedule.end_date, neverEnds)]" />
                            </template>
                            <v-date-picker v-model="$props.subscriptionSchedule.end_date" color="#c42111" scrollable />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-text-field style="display: inline-block; width: 100px;" class="mr-1" type="number" min="0" step="1" color="#c42111" label="Expires After" v-model="$props.subscriptionSchedule.expires_value" :rules="[$store.state.validationFuncs.positiveInteger($props.subscriptionSchedule.expires_value, 'Value')]">
                        </v-text-field>
                        <v-select style="display: inline-block; width: 120px;" v-model="$props.subscriptionSchedule.expires_unit" color="#c42111" :items="expiresUnitOptions" />

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon class="ml-1 mt-3" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>This determines how long the emailed link will be valid for. Once the link has expired, it will no longer work.</span>
                        </v-tooltip>
                    </v-col>
                </v-row>

            </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn text @click="$emit('cancel')">Cancel</v-btn>
            <v-btn text @click="saveClick">Save</v-btn>
        </v-card-actions>

    </v-card>

</template>

<script>
export default {
    name: 'SubscriptionSchedule',
    props: ['subscriptionSchedule'],
    components: {

    },
    data: () => ({
        users: [],
        noEmailColor: '#ff5252',
        validUserRecipientColor: '#4caf50',

        subscriptionScheduleFormValid: true,

        recurOptions: [
            {text: 'Daily', value: 'daily'},
            {text: 'Weekly', value: 'weekly'},
            {text: 'Monthly', value: 'monthly'},
            {text: 'Yearly', value: 'yearly'}
        ],
        weekDayOptions: [
            {text: 'Sunday', value: 'sunday'},
            {text: 'Monday', value: 'monday'},
            {text: 'Tuesday', value: 'tuesday'},
            {text: 'Wednesday', value: 'wednesday'},
            {text: 'Thursday', value: 'thursday'},
            {text: 'Friday', value: 'friday'},
            {text: 'Saturday', value: 'saturday'}
        ],
        recurFrequencyOptions: [],
        monthDayOptions: [],
        editScheduleTime: false,
        neverEnds: false,
        editStartDate: false,
        editEndDate: false,
        expiresUnitOptions: [
            {text: 'Days', value: 'days'},
            {text: 'Weeks', value: 'weeks'},
            {text: 'Months', value: 'months'},
            {text: 'Years', value: 'years'}
        ],
        repeatOnWeekdays: [],
        repeatOnMonthDay: "",
        validation: {
            dateOrder: (start, end, neverEnds) => ((start < end) || (neverEnds) || `End date must be after Start date`),
            weekdayRequired: (recurType, repeatOnWeekdays) => (recurType != 'weekly' || !!repeatOnWeekdays.length || `You must select at least 1 weekday`),
        },
    }),
    
    computed: {
        recurTypeText: function() {
            if(this.$props.subscriptionSchedule.recur_type == 'daily') {
                return 'days';
            }
            else if(this.$props.subscriptionSchedule.recur_type == 'weekly') {
                return 'weeks';
            }
            else if(this.$props.subscriptionSchedule.recur_type == 'monthly') {
                return 'months';
            }
            else if(this.$props.subscriptionSchedule.recur_type == 'yearly') {
                return 'years';
            }
        },

        repeatOn: function() {
            if(this.$props.subscriptionSchedule.recur_type == 'weekly') {
                return this.repeatOnWeekdays.join();
            }
            else if(this.$props.subscriptionSchedule.recur_type == 'monthly') {
                return this.repeatOnMonthDay;
            }
            return null;
        }
    },

    methods: {
        setupItems: function() {
            let users = JSON.parse(JSON.stringify(this.$store.state.batchConfig.users));
            users.forEach(u => {
                u.type = 'user';
                u.search = `${u.user_username}${u.user_fullname}`;
            });
            users.unshift({header: 'Select a user or add an email address'});
            this.users = users;

            if(this.$props.subscriptionSchedule.recur_type == 'weekly') {
                this.repeatOnWeekdays = this.$props.subscriptionSchedule.repeat_on.split(',');
            }
            else if(this.$props.subscriptionSchedule.recur_type == 'monthly') {
                this.repeatOnMonthDay = this.$props.subscriptionSchedule.repeat_on;
            }

            if(!this.$props.subscriptionSchedule.end_date) {
                this.neverEnds = true;
            }

            for(let i = 1; i <= 30; i++) {
                this.recurFrequencyOptions.push(i.toString());
                this.monthDayOptions.push(i.toString());
            }
        },

        removeRecipient: function(recipient) {
            const index = this.$props.subscriptionSchedule.recipients.indexOf(recipient);
            if(index >= 0) {
                this.$props.subscriptionSchedule.recipients.splice(index, 1);
            }
        },

        saveClick: function() {
            if(this.$refs.subscriptionScheduleForm.validate()) {
                this.$emit('save');
            }
        }
    },

    watch: {
        repeatOn: {
            handler(newVal, oldVal) {
                this.$props.subscriptionSchedule.repeat_on = this.repeatOn;
            }
        },

        neverEnds(newVal, oldVal) {
            if(newVal != oldVal && newVal == true) {
                this.$props.subscriptionSchedule.end_date = null;
            }
        }
    },

    created() {
        this.setupItems();
    },

    mounted() {
    }
};
</script>
